import "./CheckboxColumn.css";
import React from "react";
import { Button, Checkbox, message, Tooltip } from "antd";
import { LockFilled } from "@ant-design/icons";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import DeliverableIcon from "../../DeliverableIcon";
import { useOrderManagementStore } from "@/scenes/OrderManagement/store/OrderManagementStore";
import classNames from "classnames";
import { unlockOrder } from "@/services/api/order";
import { remove } from "lodash";
import OrderTag from "@/enum/OrderTag";
import ShiftTooltip from "./ShiftTooltip";
import OrderDTO from "@/dto/order/OrderDTO";
import OrderLockStore from "@/store/OrderLockStore";

interface CheckboxColumnProps {
    order: OrderDTO;
    index?: number;
}

interface LockErrorMessageProps {
    order: OrderDTO;
    orderLockStore: OrderLockStore;
}

export const LockErrorMessage: React.FC<LockErrorMessageProps> = observer(function (props) {
    const { order, orderLockStore } = props;
    const orderAddressUndeliverableLock = orderLockStore.undeliverableLockOrderIdMapping.get(order.id);
    const abnormalAddressLock = !!orderLockStore.abnormalAddressLockOrderIdMapping.get(order.id);
    const manualLock = orderLockStore.manualLockOrderIdMapping.get(order.id);
    const blacklistLock = orderLockStore.blacklistLockOrderIdMapping.get(order.id);

    const errors = [];

    if (abnormalAddressLock) {
        errors.push(
            <Link
                to="/setting/addressWarning"
                key="addressWarning"
                className="text-white"
                style={{ textDecoration: "underline" }}
            >
                地址异常
            </Link>,
        );
    }

    if (orderAddressUndeliverableLock) {
        if (errors.length) {
            errors.push(<br key="br" />);
        }

        errors.push(
            <Link
                to="/setting/undeliverableArea"
                className="text-white"
                key="undeliverableArea"
                style={{ textDecoration: "underline" }}
            >
                快递不可达:{orderAddressUndeliverableLock}
            </Link>,
        );
    }

    if (manualLock) {
        if (errors.length) {
            errors.push(<br key="br2" />);
        }

        errors.push(<span key="manualLock">手动锁单</span>);
    }

    if (blacklistLock) {
        if (errors.length) {
            errors.push(<br key="br3" />);
        }

        errors.push(<span key="blacklistLock">黑名单锁单</span>);
    }

    return <>{errors}</>;
});

const CheckboxColumn: React.FC<CheckboxColumnProps> = ({ order, index }) => {
    const orderManagementStore = useOrderManagementStore();
    const locking = !!orderManagementStore.table.orderLockStore.lockOrderIdMapping.get(order.id);
    const checked = order.isSelected === true;
    const isPartSelected = order._isPartSelected === true;

    return (
        <td
            className={classNames("checkbox-column", {
                selected: checked,
                "part-selected": isPartSelected,
            })}
        >
            {index !== null ? <p className="index">{index + 1}</p> : null}

            {locking ? (
                <div className="lock-order-container">
                    <Tooltip
                        title={() => {
                            return (
                                <LockErrorMessage
                                    order={order}
                                    orderLockStore={orderManagementStore.table.orderLockStore}
                                />
                            );
                        }}
                    >
                        <LockFilled />
                    </Tooltip>

                    <p className="unlock-button-container">
                        <Button
                            type="link"
                            onClick={async () => {
                                const manualLock = orderManagementStore.table.orderLockStore.manualLockOrderIdMapping.get(
                                    order.id,
                                );

                                if (manualLock) {
                                    await unlockOrder(order.shopId, [order]);

                                    const newTags = [...(order.tags || [])];

                                    remove(newTags, (tag) => tag === OrderTag.LOCKED);

                                    orderManagementStore.table.updateOrder(order.id, { tags: newTags });

                                    message.success("已解锁");
                                }

                                orderManagementStore.table.orderLockStore.handleUnlockOrder(order.id);
                            }}
                        >
                            解锁
                        </Button>
                    </p>
                </div>
            ) : (
                <ShiftTooltip orderId={order.id}>
                    <Checkbox
                        checked={checked}
                        onChange={(event) => {
                            const checked = event.target.checked;
                            const orderId = order.id;

                            orderManagementStore.table.changeOrderSelect(checked, orderId, index);
                        }}
                    />
                </ShiftTooltip>
            )}

            <DeliverableIcon orderId={order.id} />
        </td>
    );
};

export default observer(CheckboxColumn);
